import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import {
  DiagramComponent,
  DiagramInfo,
  CanvasSize,
  Shape,
  Component,
} from "@/types/diagram";
import { componentLibraryManager } from "@/lib/component.lib";
import { shapesLibraryManager } from "@/lib/shapes-manager";
import * as diagramComponentsLib from "@/lib/diagram-components.lib";
import { useLibrary } from "@/context/library-context";
import { useUI } from "@/context/ui-context";
import useDebounce from "@/hooks/use-debounce";
import { generateDiagramSVGContent } from "@/lib/svg-utils.lib";
import useUpdateDiagram from "@/service-hooks/use-update-diagram-service";
import { useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";

// Safe hook for UI context
const useSafeUI = () => {
  try {
    return useUI();
  } catch {
    return {
      showAttachmentPoints: false,
      canvasSettings: {
        canvas: {
          canvasSize: { width: 1000, height: 1000 },
        },
      },
    };
  }
};

interface DiagramContextType {
  // State
  diagramComponents: DiagramComponent[];
  selectedShape: string | null;
  canvasSize: CanvasSize;
  composedSVG: string;
  selectedPosition: string;
  selectedAttachmentPoint: string | null;
  availableAttachmentPoints: string[];
  isCopied: boolean;
  currentDiagramInfo: DiagramInfo | null;
  autoSaveMode: boolean;
  autoSaveState: [boolean, (mode: boolean) => void];
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
  isDiagramLoading: boolean;

  // Actions
  setDiagramComponents: (components: DiagramComponent[]) => void;
  selectShape: (id: string | null) => void;
  setCanvasSize: (size: CanvasSize) => void;
  setComposedSVG: (svg: string) => void;
  setSelectedPosition: (position: string) => void;
  setSelectedAttachmentPoint: (point: string | null) => void;
  setAvailableAttachmentPoints: (points: string[]) => void;
  setIsCopied: (copied: boolean) => void;
  setCurrentDiagramInfo: (diagramInfo: DiagramInfo | null) => void;
  setAutoSaveMode: (mode: boolean) => void;
  setIsDiagramLoading: (loading: boolean) => void;

  // Complex operations
  add3DShape: (shapeName: string, svgLibrary: Shape[]) => void;
  add2DShape: (
    shapeName: string,
    attachTo: string,
    svgLibrary: Shape[]
  ) => void;
  addComponent: (component: Component) => void;
  remove3DShape: (id: string) => void;
  remove2DShape: (parentId: string, shapeIndex: number) => void;
  cut3DShape: (id: string) => void;
  copy3DShape: (id: string) => void;
  cancelCut3DShape: (id: string) => void;
  paste3DShape: (id: string) => void;
  updateMetadata: (id: string, type: string | undefined, metadata: any) => void;

  // Repositioning operations
  move3DShape: (shapeId: string, newParentId: string | null, newPosition: string) => void;
  getAvailableMoveTargets: (shapeId: string) => { targetId: string; targetName: string; positions: string[] }[];
}

const DiagramContext = createContext<DiagramContextType | undefined>(undefined);

interface DiagramProviderProps {
  children: ReactNode;
  onAddHistory?: (components: DiagramComponent[]) => void;
}

export const DiagramProvider: React.FC<DiagramProviderProps> = ({
  children,
  onAddHistory,
}) => {
  const library = useLibrary();
  const { showAttachmentPoints, canvasSettings } = useSafeUI();
  const [diagramComponents, setDiagramComponents] = useState<
    DiagramComponent[]
  >([]);
  const [selectedShape, setSelectedShape] = useState<string | null>(null);
  const [canvasSize, setCanvasSize] = useState<CanvasSize>(() => {
    return canvasSettings?.canvas?.canvasSize || { width: 1000, height: 1000 };
  });
  const [composedSVG, setComposedSVG] = useState<string>("");
  const [selectedPosition, setSelectedPosition] = useState<string>("top");
  const [selectedAttachmentPoint, setSelectedAttachmentPoint] = useState<
    string | null
  >(null);
  const [availableAttachmentPoints, setAvailableAttachmentPoints] = useState<
    string[]
  >([]);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [currentDiagramInfo, setCurrentDiagramInfo] =
    useState<DiagramInfo | null>(null);
  const [autoSaveMode, setAutoSaveMode] = useState<boolean>(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [isDiagramLoading, setIsDiagramLoading] = useState<boolean>(false);

  // Autosave functionality
  const auth = useAuth();
  const { id: uuid } = useParams();
  const debouncedDiagramComponents = useDebounce(diagramComponents, 3000); // 3 second delay
  const debouncedCanvasSize = useDebounce(canvasSize, 3000);

  // Update diagram service for autosave
  const { mutate: updateDiagram } = useUpdateDiagram(
    (_, error) => {
      if (error) {
        console.error('Autosave failed:', error);
        setAutoSaveStatus('error');
        // Reset to idle after 3 seconds to allow retry
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } else {
        setAutoSaveStatus('saved');
        // Reset to idle after 2 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      }
    }
  );

  // Autosave effect
  useEffect(() => {
    // Only autosave if:
    // 1. Autosave mode is enabled
    // 2. We have a current diagram info (existing diagram)
    // 3. User is authenticated
    // 4. Diagram has components (not empty)
    // 5. User owns the diagram
    // 6. Components or canvas size actually changed (debounced)
    if (
      autoSaveMode &&
      currentDiagramInfo?._id &&
      auth.user?.profile?.sub &&
      debouncedDiagramComponents.length > 0 &&
      auth.user.profile.sub === currentDiagramInfo.userId &&
      autoSaveStatus !== 'saving' // Prevent multiple simultaneous saves
    ) {
      setAutoSaveStatus('saving');
      const svgContent = generateDiagramSVGContent(composedSVG, debouncedCanvasSize);
      
      updateDiagram({
        id: currentDiagramInfo._id,
        name: currentDiagramInfo.name || 'Untitled Diagram',
        description: currentDiagramInfo.metadata?.description || '',
        diagramComponents: debouncedDiagramComponents,
        svgContent,
        uuid: uuid || currentDiagramInfo.uuid || '',
      });
    }
  }, [debouncedDiagramComponents, debouncedCanvasSize, autoSaveMode, currentDiagramInfo, auth.user, composedSVG, updateDiagram, uuid, autoSaveStatus]);

  // Wrapper function that handles clearing selected shape when diagram is empty
  const handleSetDiagramComponents = useCallback(
    (components: DiagramComponent[]) => {
      setDiagramComponents(components);
      // Clear selected shape if diagram is empty or if selected shape no longer exists
      if (components.length === 0) {
        setSelectedShape(null);
        setAvailableAttachmentPoints([]);
      } else if (selectedShape) {
        // Check if currently selected shape still exists in the new components
        const shapeExists = components.some(
          (component) => component.id === selectedShape
        );
        if (!shapeExists) {
          setSelectedShape(null);
          setAvailableAttachmentPoints([]);
        }
      }
    },
    [selectedShape]
  );

  const selectShape = useCallback(
    (id: string | null) => {
      if (!id) {
        setSelectedShape(null);
        setAvailableAttachmentPoints([]);
      } else {
        const selectedComponent = diagramComponentsLib.get3DShape(
          diagramComponents,
          id
        );

        if (selectedComponent && !selectedComponent.cut) {
          setSelectedShape(id);
          const updatedAttachmentPoints =
            diagramComponentsLib.updateAvailableAttachmentPoints(
              selectedComponent
            );
          setAvailableAttachmentPoints(updatedAttachmentPoints);
        } else {
          setAvailableAttachmentPoints([]);
        }
      }
    },
    [diagramComponents]
  );

  const add3DShape = useCallback(
    (shapeName: string, svgLibrary: Shape[]) => {
      // Find the shape in the library
      const shape =
        shapesLibraryManager.getShape(shapeName) ||
        svgLibrary.find((s) => s.name === shapeName);

      if (!shape) {
        console.error(`Shape ${shapeName} not found in library`);
        return;
      }

      // Ensure shape is deserialized into shapes library manager
      shapesLibraryManager.deserializeShapesLib([shape]);

      const result = diagramComponentsLib.add3DShape(
        diagramComponents,
        svgLibrary,
        shapeName,
        selectedPosition,
        selectedAttachmentPoint,
        selectedShape
      );

      if (result.newComponent) {
        setDiagramComponents(result.updatedComponents);
        // Set the selected shape directly since selectShape uses the old diagramComponents
        setSelectedShape(result.newComponent.id);
        // Calculate attachment points for the new component
        const updatedAttachmentPoints =
          diagramComponentsLib.updateAvailableAttachmentPoints(
            result.newComponent
          );
        setAvailableAttachmentPoints(updatedAttachmentPoints);
        onAddHistory?.(result.updatedComponents);
      } else {
        console.error("Failed to add new 3D shape");
      }
    },
    [
      diagramComponents,
      selectedPosition,
      selectedAttachmentPoint,
      selectedShape,
      selectShape,
      onAddHistory,
    ]
  );

  const add2DShape = useCallback(
    (shapeName: string, attachTo: string, svgLibrary: Shape[]) => {
      const shape =
        shapesLibraryManager.getShape(shapeName) ||
        svgLibrary.find((s) => s.name === shapeName);

      if (!shape) {
        console.error(`Shape ${shapeName} not found in library`);
        return;
      }

      shapesLibraryManager.deserializeShapesLib([shape]);
      const updatedComponents = diagramComponentsLib.add2DShape(
        diagramComponents,
        selectedShape,
        shapeName,
        attachTo,
        selectedPosition,
        selectedAttachmentPoint
      );

      setDiagramComponents(updatedComponents);
      onAddHistory?.(updatedComponents);
    },
    [
      diagramComponents,
      selectedShape,
      selectedPosition,
      selectedAttachmentPoint,
      onAddHistory,
    ]
  );

  const addComponent = useCallback(
    (component: Component) => {
      if (!componentLibraryManager.getComponent(component.id)) {
        componentLibraryManager.deserializeComponentLib([component]);
      }

      const result = diagramComponentsLib.addComponentToScene(
        diagramComponents,
        component.id,
        selectedPosition,
        selectedAttachmentPoint,
        selectedShape
      );

      if (result.newComponent) {
        setDiagramComponents(result.updatedComponents);
        // Set the selected shape directly since selectShape uses the old diagramComponents
        setSelectedShape(result.newComponent.id);
        // Calculate attachment points for the new component
        const updatedAttachmentPoints =
          diagramComponentsLib.updateAvailableAttachmentPoints(
            result.newComponent
          );
        setAvailableAttachmentPoints(updatedAttachmentPoints);
        onAddHistory?.(result.updatedComponents);
      } else {
        console.error("Failed to add new component");
      }
    },
    [
      diagramComponents,
      selectedPosition,
      selectedAttachmentPoint,
      selectedShape,
      selectShape,
      onAddHistory,
    ]
  );

  const remove3DShape = useCallback(
    (id: string) => {
      const updatedComponents = diagramComponentsLib.remove3DShape(
        diagramComponents,
        id
      );
      setDiagramComponents(updatedComponents);
      onAddHistory?.(updatedComponents);

      // If removed component was currently selected, select last component or none
      if (selectedShape === id) {
        if (updatedComponents.length > 0) {
          selectShape(updatedComponents[updatedComponents.length - 1].id);
        } else {
          selectShape(null);
        }
      }
    },
    [diagramComponents, selectedShape, selectShape, onAddHistory]
  );

  const remove2DShape = useCallback(
    (parentId: string, shapeIndex: number) => {
      const updatedComponents = diagramComponentsLib.remove2DShape(
        diagramComponents,
        parentId,
        shapeIndex
      );
      setDiagramComponents(updatedComponents);
      onAddHistory?.(updatedComponents);
    },
    [diagramComponents, onAddHistory]
  );

  const cut3DShape = useCallback(
    (id: string) => {
      const updatedComponents = diagramComponentsLib.cut3DShape(
        diagramComponents,
        id
      );
      setDiagramComponents(updatedComponents);
      setSelectedShape(null);
    },
    [diagramComponents]
  );

  const copy3DShape = useCallback(
    (id: string) => {
      setIsCopied(true);
      const updatedComponents = diagramComponentsLib.cut3DShape(
        diagramComponents,
        id
      );
      setDiagramComponents(updatedComponents);
    },
    [diagramComponents]
  );

  const cancelCut3DShape = useCallback(
    (id: string | null) => {
      const updatedComponents = diagramComponentsLib.cancelCut(
        diagramComponents,
        id
      );
      setDiagramComponents(updatedComponents);
      setIsCopied(false);
    },
    [diagramComponents]
  );

  const paste3DShape = useCallback(
    (id: string) => {
      if (selectedShape) {
        let result = null;

        if (isCopied) {
          const copiedShapes = diagramComponentsLib.cancelCut(
            diagramComponentsLib.copy3DShape(diagramComponents, null),
            null
          );
          result = diagramComponentsLib.pasteCopied3DShapes(
            diagramComponents,
            copiedShapes,
            selectedShape,
            selectedPosition,
            selectedAttachmentPoint
          );
        } else {
          result = diagramComponentsLib.pasteCut3DShapes(
            diagramComponents,
            id,
            selectedShape,
            selectedPosition,
            selectedAttachmentPoint
          );
        }

        if (result.pastedComponent) {
          setDiagramComponents(result.updatedComponents);
          selectShape(result.pastedComponent.id);
        } else {
          console.error("Failed to paste 3D shape");
        }
      }
    },
    [
      diagramComponents,
      selectedShape,
      selectedPosition,
      selectedAttachmentPoint,
      isCopied,
      selectShape,
    ]
  );

  const updateMetadata = useCallback(
    (id: string, type: string | undefined, metadata: any) => {
      setDiagramComponents((prevComponents) =>
        prevComponents.map((component) => {
          if (component.id === id) {
            return {
              ...component,
              type,
              metadata: type ? metadata : undefined,
            };
          }
          return component;
        })
      );
    },
    []
  );

  // Repositioning operations
  const move3DShape = useCallback(
    (shapeId: string, newParentId: string | null, newPosition: string) => {
      const updatedComponents = diagramComponentsLib.move3DShape(
        diagramComponents,
        shapeId,
        newParentId,
        newPosition as DiagramComponent["position"]
      );
      setDiagramComponents(updatedComponents);
      onAddHistory?.(updatedComponents);
    },
    [diagramComponents, onAddHistory]
  );

  const getAvailableMoveTargetsFn = useCallback(
    (shapeId: string) => {
      return diagramComponentsLib.getAvailableMoveTargets(diagramComponents, shapeId);
    },
    [diagramComponents]
  );

  // Automatically generate composed SVG when diagram components change
  useEffect(() => {
    const { svgContent, processedComponents } =
      diagramComponentsLib.compileDiagram(
        diagramComponents,
        canvasSize,
        library.svgLibrary,
        showAttachmentPoints
      );
    setComposedSVG(svgContent);

    // Only update if the processed components are different
    if (
      !diagramComponentsLib.areComponentArraysEqual(
        processedComponents,
        diagramComponents
      )
    ) {
      setDiagramComponents(processedComponents);
    }
  }, [
    diagramComponents,
    canvasSize,
    library.svgLibrary,
    showAttachmentPoints,
    library.components,
  ]);

  // Sync canvas size with UI context settings
  useEffect(() => {
    if (canvasSettings?.canvas?.canvasSize) {
      setCanvasSize(canvasSettings.canvas.canvasSize);
    }
  }, [canvasSettings?.canvas?.canvasSize]);

  const value: DiagramContextType = {
    // State
    diagramComponents,
    selectedShape,
    canvasSize,
    composedSVG,
    selectedPosition,
    selectedAttachmentPoint,
    availableAttachmentPoints,
    isCopied,
    currentDiagramInfo,
    autoSaveMode,
    autoSaveState: [autoSaveMode, setAutoSaveMode],
    autoSaveStatus,
    isDiagramLoading,

    // Actions
    setDiagramComponents: handleSetDiagramComponents,
    selectShape,
    setCanvasSize,
    setComposedSVG,
    setSelectedPosition,
    setSelectedAttachmentPoint,
    setAvailableAttachmentPoints,
    setIsCopied,
    setCurrentDiagramInfo,
    setAutoSaveMode,
    setIsDiagramLoading,

    // Complex operations
    add3DShape,
    add2DShape,
    addComponent,
    remove3DShape,
    remove2DShape,
    cut3DShape,
    copy3DShape,
    cancelCut3DShape,
    paste3DShape,
    updateMetadata,

    // Repositioning operations
    move3DShape,
    getAvailableMoveTargets: getAvailableMoveTargetsFn,
  };

  return (
    <DiagramContext.Provider value={value}>{children}</DiagramContext.Provider>
  );
};

export const useDiagram = (): DiagramContextType => {
  const context = useContext(DiagramContext);
  if (!context) {
    throw new Error("useDiagram must be used within a DiagramProvider");
  }
  return context;
};
